// src/pages/Services.js
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import "../styles/Services.css";
import whatIDoImg from "../assets/what-i-do.png";
import arrowImg from "../assets/what-i-do-arrow.png";
import Modal from "../components/Modal";
import Footer from "../components/Footer";

// Firestore
import { db } from "../firebase";
import { doc, getDoc, collection, getDocs } from "firebase/firestore";

// Local icon fallbacks (used if Firestore iconUrl is empty)
import bulbIcon from "../assets/icons/bulb.png";
import asterixIcon from "../assets/icons/asterix.png";
import hashtagIcon from "../assets/icons/hashtag.png";
import heartIcon from "../assets/icons/heart.png";
import starIcon from "../assets/icons/star.png";

// Map common service titles → local fallback icon
const iconFallbackMap = {
  "Content Strategy": bulbIcon,
  "AI Tools Proficiency": asterixIcon,
  "Discord Server Creation & Management": hashtagIcon,
  "UI/UX Design": heartIcon,
  "Presentation Design": starIcon,
  "Content Writing": bulbIcon,
  "Video Editing": asterixIcon,
  "Social Media Creatives": heartIcon,
};

function normalizeList(snap, fallbackColor) {
  if (!snap) return [];
  const items = snap.docs
    .map((d) => {
      const x = d.data() || {};
      const title = x.title || "";
      const explicitIcon = x.iconUrl || "";
      const fallbackIcon = iconFallbackMap[title] || "";

      // Support either `order` or legacy `number`
      const rawOrder = x.order ?? x.number ?? "";
      const numSort =
        typeof rawOrder === "number" ? rawOrder : parseFloat(rawOrder) || 999;

      return {
        number: String(rawOrder ?? ""),
        _numSort: numSort,
        color: x.color || fallbackColor,
        title,
        description: x.description || "",
        popupContentHTML: x.popupContentHTML || "",
        icon: explicitIcon || fallbackIcon, // prefer Firestore icon
        _active: x.active !== false, // default true if missing
      };
    })
    .filter((it) => it._active)
    .sort((a, b) => a._numSort - b._numSort);

  return items;
}

/**
 * Try subcollection first:
 *   pageContent/services/<subName>
 * If empty, fall back to root collection:
 *   <subName>
 * subName should be "servicesStrategy" or "servicesVisual".
 */
async function fetchServicesPreferSub(db, subName) {
  // 1) Subcollection
  try {
    const subRef = collection(db, "pageContent", "services", subName);
    const subSnap = await getDocs(subRef);
    if (!subSnap.empty) return subSnap;
  } catch (_) {
    // ignore and fall back
  }

  // 2) Root fallback
  try {
    const rootRef = collection(db, subName);
    const rootSnap = await getDocs(rootRef);
    return rootSnap;
  } catch (e) {
    console.error(`Error fetching ${subName}:`, e);
    return null;
  }
}

const Services = () => {
  const [selectedService, setSelectedService] = useState(null);
  const [heroData, setHeroData] = useState({
    heading: "Services",
    subheading: "Offering tailored solutions with attention to detail.",
    backgroundImage: "",
    ctaText: "Let’s Collaborate",
    ctaLink: "/contact",
  });

  const [serviceCategories, setServiceCategories] = useState([
    { title: "Strategy & Structure", services: [] },
    { title: "Visual & Creative", services: [] },
  ]);

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        // 1) Hero/CTA
        try {
          const pageSnap = await getDoc(doc(db, "pageContent", "services"));
          if (pageSnap.exists() && mounted) {
            const d = pageSnap.data();
            setHeroData((prev) => ({
              heading: d.heroTitle || prev.heading,
              subheading: d.heroSubtitle || prev.subheading,
              backgroundImage: d.heroBackground || prev.backgroundImage,
              ctaText: d.letsCollaborateText || prev.ctaText,
              ctaLink: d.letsCollaborateLink || prev.ctaLink,
            }));
          }
        } catch (e) {
          console.warn("Services hero not found (pageContent/services). Using defaults.");
        }

        // 2) Strategy services
        const stratSnap = await fetchServicesPreferSub(db, "servicesStrategy");
        const strategy = normalizeList(stratSnap, "blue");

        // 3) Visual services
        const visSnap = await fetchServicesPreferSub(db, "servicesVisual");
        const visual = normalizeList(visSnap, "pink");

        if (mounted) {
          setServiceCategories([
            { title: "Strategy & Structure", services: strategy },
            { title: "Visual & Creative", services: visual },
          ]);
        }
      } catch (err) {
        console.error("Error fetching Services data:", err);
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  return (
    <div className="services-container">
      {/* HERO */}
      <div
        className="services-hero"
        style={{
          backgroundImage: heroData.backgroundImage
            ? `url(${heroData.backgroundImage})`
            : undefined,
        }}
      >
        <h1>{heroData.heading}</h1>
        <p>{heroData.subheading}</p>
      </div>

      <div className="intro-wrapper">
        <img src={whatIDoImg} alt="What I do?" className="what-i-do-img" />
        <img src={arrowImg} alt="arrow" className="what-i-do-arrow" />
      </div>

      {/* CATEGORIES */}
      {loading ? (
        <div className="services-frame" aria-busy="true" />
      ) : (
        serviceCategories.map((category, catIndex) => (
          <div key={catIndex} className="service-wrapper">
            <h2 className="section-heading">
              {category.title}
              <img
                src={require("../assets/underline.png")}
                alt="underline"
                className={`underline-img ${
                  catIndex === 0 ? "strategy-underline" : "creative-underline"
                }`}
              />
            </h2>

            <div className="services-frame">
              <div className="service-content">
                {category.services.length === 0 ? (
                  <p style={{ paddingLeft: "3.5rem", color: "#333" }}>
                    More services coming soon.
                  </p>
                ) : (
                  category.services.map((service, index) => (
                    <div
                      className="service-item"
                      key={`${catIndex}-${index}`}
                      onClick={() =>
                        setSelectedService({
                          ...service,
                          index,
                          category: catIndex === 0 ? "strategy" : "visual",
                        })
                      }
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          setSelectedService({
                            ...service,
                            index,
                            category: catIndex === 0 ? "strategy" : "visual",
                          });
                        }
                      }}
                    >
                      <div className={`service-number ${service.color}`}>
                        {service.number}
                      </div>
                      <div className="service-text">
                        <h3>{service.title}</h3>
                        <p>{service.description}</p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        ))
      )}

      <div className="footer-spacer">
        <Link to={heroData.ctaLink} className="collab-button">
          {heroData.ctaText}
        </Link>
      </div>

      <Footer />

      {/* MODAL */}
      {selectedService && (
        <Modal
          title={selectedService.title}
          content={
            selectedService.popupContentHTML || selectedService.description
          }
          icon={selectedService.icon}
          onClose={() => setSelectedService(null)}
          index={selectedService.index}
          category={selectedService.category}
        />
      )}
    </div>
  );
};

export default Services;
